import { gql } from '@apollo/client';
import { PageHeader } from '@components/Text';
import Link from 'next/link';
import Layout from '@components/Layout';
import { ContentPadding } from '@components/ContentPadding';
import { useMemo, useState } from 'react';
import DropdownFilter from '@components/DropdownList';
import { TextLink } from '@components/Link';
import { Media } from '@components/Media';
import { differenceInMilliseconds } from 'date-fns';
import { useGetPastCollectionsQuery } from 'generated/graphql';
import { COMMISSIONS } from 'logic/commissions';
import { seasonNumberDisplay } from '@components/SeasonNumber';
import { routes } from '@utils/routes';

export default function Collections() {
  const [searchText, setSearchText] = useState('');
  const [commissionFilter, setCommissionFilter] = useState('');
  const [seasonFilter, setSeasonFilter] = useState('');
  const { data } = useGetPastCollectionsQuery();
  const collections = useMemo(() => {
    const communityPages =
      data?.commissionSeasonCollection.items
        .filter((item) => {
          if (commissionFilter && COMMISSIONS.community !== commissionFilter) {
            return false;
          }

          if (seasonFilter) {
            return false;
          }

          /**
           * When searching, look for matches to search term on three fields
           * project name, artist name and description
           */
          if (
            searchText.length > 0 &&
            !item.name.toLowerCase().includes(searchText.toLowerCase()) &&
            !item.description.toLowerCase().includes(searchText.toLowerCase())
          ) {
            return false;
          }

          return true;
        })
        .map((season) => {
          // todo: pick correct image
          const collection = data.communityCollections?.items.find(
            (collection) =>
              collection.season.seasonNumber === season.seasonNumber
          );
          return {
            ...season,
            image: collection.image,
          };
        }) ?? [];
    const collections =
      data?.collectionCollection.items.filter((item) => {
        if (
          commissionFilter &&
          item.season?.commission?.name !== commissionFilter
        ) {
          return false;
        }

        if (
          seasonFilter &&
          item.season?.seasonNumber !== Number(seasonFilter)
        ) {
          return false;
        }

        /**
         * When searching, look for matches to search term on three fields
         * project name, artist name and description
         */
        if (
          searchText.length > 0 &&
          !item.projectName.toLowerCase().includes(searchText.toLowerCase()) &&
          !item.name.toLowerCase().includes(searchText.toLowerCase()) &&
          !item.projectDescription
            .toLowerCase()
            .includes(searchText.toLowerCase())
        ) {
          return false;
        }

        return true;
      }) ?? [];
    return [...communityPages, ...collections].sort((a, b) => {
      return differenceInMilliseconds(
        new Date(b.droptime),
        new Date(a.droptime)
      );
    });
  }, [
    data?.collectionCollection,
    data?.commissionSeasonCollection,
    data?.communityCollections,
    searchText,
    commissionFilter,
    seasonFilter,
  ]);

  const availableSeasons = useMemo(
    () =>
      Array.from(
        new Set(
          data?.collectionCollection?.items.map((item) =>
            item.season?.seasonNumber.toString()
          )
        )
      ).map((number) => ({
        value: number,
        label: seasonNumberDisplay[number],
      })),
    [data?.collectionCollection?.items]
  );

  const availableCommissions = useMemo(
    () => [
      { label: 'All', value: '' },
      ...Array.from(
        new Set(
          data?.collectionCollection?.items.map(
            (item) => item.season?.commission.name
          )
        )
      ),
    ],
    [data?.collectionCollection?.items]
  );

  return (
    <Layout>
      <ContentPadding>
        <PageHeader className="mb-16">Collections</PageHeader>
        <input
          type="text"
          className="w-full border-2 border-black mb-6 p-5 mx-auto"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <div className="mx-4 text-black font-bold sm:inline-flex">
          <span className="mr-8">Filter By:</span>
          {/* {allPhotographers && allPhotographers?.length > 1 && (
          <DropdownFilter
            title="photographer"
            options={allPhotographers}
            selected={photographer}
            setSelected={setPhotographer}
          />
        )} */}
          {/* TODO: configurable */}
          <DropdownFilter
            title="Commission"
            options={availableCommissions}
            selected={commissionFilter}
            setSelected={setCommissionFilter}
          />
          <DropdownFilter
            title="Season Number"
            options={availableSeasons}
            selected={seasonFilter}
            setSelected={setSeasonFilter}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mt-4 w-full gap-x-5 gap-y-14 mx-auto">
          {collections.map((item) => {
            const href =
              item.__typename === 'CommissionSeason'
                ? `/community-commission/${item.seasonNumber}`
                : routes.collectionDetails(item.slug);
            const name = 'projectName' in item ? item.projectName : item.name;
            const subtitle = item.__typename === 'Collection' ? item.name : '';

            return (
              <Link href={href} key={item.slug} passHref>
                <div className="my-4 sm:my-0 flex flex-1 flex-col">
                  {item.image.url && (
                    <div className="relative my-auto sm:row-span-4 cursor-pointer flex-1 justify-end">
                      <Media
                        height="100%"
                        width="100%"
                        layout="responsive"
                        placeholder="blur"
                        className="w-full object-contain object-bottom justify-end"
                        alt=""
                        quality={50}
                        src={item.image.url}
                      />
                    </div>
                  )}
                  <div className="sm:row-span-1">
                    <p className="font-bold mt-1 no-i18n">{name}</p>
                    <div className="flex justify-between mb-6">
                      <p>{subtitle}</p>
                      {'season' in item && (
                        <p className="no-i18n">
                          {item.season.commission.name} Commission
                        </p>
                      )}
                    </div>
                    <TextLink href={href}>View Collection</TextLink>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </ContentPadding>
    </Layout>
  );
}

gql`
  query GetPastCollections {
    commissionSeasonCollection(where: { commission: { slug: "community" } }) {
      items {
        slug
        description
        name
        seasonNumber
        droptime
      }
    }
    communityCollections: collectionCollection(
      order: droptime_DESC
      where: { commission: { slug: "community" } }
    ) {
      items {
        droptime
        image {
          url
        }
        season {
          seasonNumber
        }
      }
    }
    collectionCollection(
      order: droptime_DESC
      where: { commission: { slug_not: "community" }, image_exists: true }
    ) {
      items {
        slug
        projectDescription
        name
        projectName
        droptime
        image {
          url
        }
        season {
          seasonNumber
          commission {
            slug
            name
          }
        }
      }
    }
  }
`;
